<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">
          FNOL
          <small>(First Notification of Loss)</small>
        </h1>
      </template>
      <template #actions>
        <page-header-action
          tag="form-button"
          :indicator="indicator"
          @clicked="sendToCreateView"
          v-if="!readonly"
        >
          New FNOL
        </page-header-action>
      </template>
    </page-header>
    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <div class="loading-overlay" v-if="loading"></div>
          <div class="card-header">
            <div style="width: 100%; margin: 0;">
              <selectlist
                name="handler_id"
                label="Handler"
                label-name="full_name"
                :options="handlers"
                :value="filters.handler_id"
                @input="setHandler"
                :stacked="true"
              ></selectlist>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Handler</th>
                  <th>Claim Reference</th>
                  <th>Claim Type</th>
                  <th>Client Name</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody v-if="!!incidents && incidents.length">
                <tr v-bind:key="index" v-for="(incident, index) in incidents">
                  <td>
                    {{ incident.handler?.full_name || 'Not Assigned' }}
                  </td>
                  <td>
                    {{ incident.source?.claim_reference || 'Not Provided' }}
                  </td>
                  <td>
                    {{
                      incident.source?.incident_claim_type?.label ||
                        'Not Provided'
                    }}
                  </td>
                  <td>
                    {{ incident.client?.full_name || 'Not Provided' }}
                  </td>
                  <td class="text-right">
                    <button
                      class="btn btn-primary"
                      v-on:click.prevent="assignIncidentToHandler(incident)"
                      v-if="hasPermission('claims.reassign')"
                    >
                      {{
                        incident.handler ? 'Re-Assign Handler' : 'Not Assigned'
                      }}
                    </button>
                    <router-link
                      class="btn btn-primary"
                      :to="`/first-notice-of-loss/${incident.identifier}`"
                    >
                      View FNOL
                    </router-link>
                    <form-button
                      :indicator="incident.indicator"
                      level="danger"
                      v-if="hasPermission('claims.archive')"
                      v-on:clicked="archive(incident)"
                    >
                      Archive
                    </form-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="!incidents.length">
                <tr>
                  <td colspan="4" class="text-center">
                    Please click the "New FNOL" button to add a new First
                    Notification of Loss
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="card-footer text-right" v-show="showPagination">
            <pagination
              :data="paginator_data"
              v-on:updateResults="getData"
              filter="stage"
              filterParams="first_notice_of_loss"
            ></pagination>
          </div>
        </div>
      </div>
    </div>

    <assign-handler
      :incident="incident"
      v-if="incident"
      :handlers="handlers"
      :show="modals.assign"
      v-on:close="closeAssignToHandlerModal"
      v-on:assigned="handlerWasAssigned"
    ></assign-handler>
  </div>
</template>

<script>
import AssignHandler from './../Shared/AssignHandler.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  data() {
    return {
      loading: false,
      incident: null,
      incidents: [],
      paginator_data: {},
      reasons: [],
      filters: {
        handler_id: null
      },
      indicator: false,
      archiveIndicator: false,
      modals: {
        assign: false
      }
    };
  },

  components: {
    AssignHandler,
    PageHeader,
    PageHeaderAction
  },

  mounted() {
    this.$store.commit('ClaimHandlers/retrieve');

    window.Ajax.get(`/incidents/reasons?filter[status]=rejected`)
      .then(response => {
        this.reasons = response.data;
      })
      .catch(error => {});

    this.findIncidents();
  },

  computed: {
    handlers() {
      return this.$store.getters['ClaimHandlers/all'];
    },

    showPagination() {
      return (
        this.paginator_data.next_page_url || this.paginator_data.prev_page_url
      );
    },

    readonly() {
      return this.isReadonly();
    }
  },

  methods: {
    archive(incident) {
      // If the user does not have permission to archive a claim then short-circuit
      if (!this.hasPermission('claims.archive')) {
        return;
      }

      const options = {};

      this.reasons.forEach((reason, key) => {
        options[key] = reason.body;
      });

      window
        .swal({
          title: 'Please Confirm',
          text: 'Are you sure you want archive this claim?',
          type: 'warning',
          input: 'select',
          inputOptions: options,
          inputPlaceholder: 'Select a Reason',
          inputValidator: value => {
            return new Promise((resolve, reject) => {
              if (value) {
                resolve(value);
              } else {
                reject('You must select a reason.');
              }
            });
          },
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
        .then(reason => {
          const reasonId = this.reasons[reason]?.id;

          if (reasonId) {
            this.$set(incident, 'indicator', true);

            window.Ajax.delete(
              `/incidents/${incident.identifier}/archive?reason=${reasonId}`
            )
              .then(response => {
                incident.indicator = false;
                window.toastify.success(
                  'Archived',
                  'The incident was archived successfully!'
                );
                this.findIncidents();
              })
              .catch(error => {
                window.toastify.error(
                  'Error',
                  'Sorry, there was an error archiving the incident'
                );
              });
          }
        })
        .catch(() => {});
    },

    getData(url) {
      Object.keys(this.filters).forEach(key => {
        url = `${url}&filter[${key}]=${this.filters[key]}`;
      });

      this.loading = true;

      window.Ajax.get(url)
        .then(response => {
          this.incidents = response.data.data;
          this.paginator_data = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
        });
    },

    setHandler(handlerId) {
      this.filters.handler_id = handlerId;

      this.getData(
        `/incidents?filter[stage]=first_notice_of_loss&per_page=${this.perPage}`
      );
    },

    viewIncident(incident) {
      if (
        incident.credit_hire &&
        incident.credit_hire.has_vehicle_hire_agreement
      ) {
        this.sendToComplete(incident.identifier);
        return;
      }

      this.sendToRoute(incident.identifier);
    },

    findIncidents() {
      this.getData(
        `/incidents?filter[stage]=first_notice_of_loss&per_page=${this.perPage}`
      );
    },

    closeAssignToHandlerModal() {
      this.modals.assign = false;
    },

    handlerWasAssigned() {
      this.modals.assign = false;
      this.incident = null;
      this.findIncidents();

      window.toastify.success('Assigned', 'The case was assigned.');
    },

    assignIncidentToHandler(incident) {
      this.incident = incident.identifier;
      this.modals.assign = true;
    },

    sendToCreateView() {
      this.indicator = true;
      window.Ajax.post('/incidents', {})
        .then(response => {
          this.indicator = false;
          this.sendToRoute(response.data.identifier);
        })
        .catch(error => {
          this.indicator = false;
          window.toastify.error(
            'Sorry, there was an issue creating the first notice of loss'
          );
        });
    },

    sendToRoute(identifier) {
      this.$router.push('/first-notice-of-loss/' + identifier);
    },

    sendToComplete(identifier) {
      this.$router.push('/first-notice-of-loss/' + identifier + '/complete');
    }
  }
};
</script>
